import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Cloud, Wind, Waves, Sun, Moon, Droplets, Gauge, Thermometer, CloudRain, Sunrise, Sunset, Compass, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const WMO_EN = {
  0: "Clear sky", 1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog", 48: "Rime fog",
  51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain",
  66: "Freezing rain", 67: "Freezing rain (heavy)",
  71: "Light snow", 73: "Snow", 75: "Heavy snow",
  77: "Snow grains",
  80: "Rain showers", 81: "Heavy showers", 82: "Violent showers",
  85: "Snow showers", 86: "Heavy snow showers",
  95: "Thunderstorm", 96: "Thunderstorm + hail", 99: "Heavy thunderstorm",
};

const compass16 = (deg) => {
  if (deg == null) return "—";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
};

const fmtTime = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtDay  = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }); } catch { return iso; } };

export default function Weather() {
  const t = useT();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/weather/live");
        setLive(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Weather unavailable");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="ledger-card p-8 text-center text-[#94A3B8]" data-testid="weather-loading">{t("weather.loading")}</div>;
  if (!live)   return <div className="ledger-card p-8 text-center text-[#F87171]" data-testid="weather-error">{t("weather.loadFail")}</div>;

  return (
    <div className="space-y-6" data-testid="weather-page">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#4DA6FF]">{t("weather.section")}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("weather.title")}</h1>
          <div className="font-mono text-[11px] text-[#64748B]">{live.location?.name} · {live.location?.lat?.toFixed(3)}°N, {live.location?.lon?.toFixed(3)}°E</div>
        </div>
        <div className="font-mono text-[10px] text-[#94A3B8]">{t("weather.updated")} {fmtTime(live.observation_time || live.recorded_at)}</div>
      </div>

      {/* Current conditions */}
      <div className="ledger-card p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="weather-current">
          <Metric icon={Thermometer} label={t("weather.temp")} value={live.temp_c != null ? `${live.temp_c.toFixed(1)} °C` : "—"} hint={live.apparent_c != null ? `${t("weather.feelsLike")} ${live.apparent_c.toFixed(1)} °C` : ""} />
          <Metric icon={Wind} label={t("weather.wind")} value={live.wind_kt != null ? `${live.wind_kt} kt` : "—"} hint={`${compass16(live.wind_dir_deg)} · ${t("weather.gust")} ${live.gust_kt ?? "—"} kt`} />
          <Metric icon={Waves} label={t("weather.wave")} value={live.wave_m != null ? `${live.wave_m} m` : "—"} hint={`${compass16(live.wave_dir_deg)} · ${live.wave_period_s ?? "—"} s`} />
          <Metric icon={Thermometer} label={t("weather.seaTemp")} value={live.sea_temp_c != null ? `${live.sea_temp_c.toFixed(1)} °C` : "—"} hint="" />
          <Metric icon={Cloud} label={t("weather.cloud")} value={live.cloud_pct != null ? `${live.cloud_pct} %` : "—"} hint={WMO_EN[live.weather_code] || "—"} />
          <Metric icon={CloudRain} label={t("weather.precip")} value={live.precipitation_mm != null ? `${live.precipitation_mm.toFixed(1)} mm` : "—"} hint="" />
          <Metric icon={Gauge} label={t("weather.pressure")} value={live.pressure_hpa != null ? `${Math.round(live.pressure_hpa)} hPa` : "—"} hint="" />
          <Metric icon={Droplets} label={t("weather.humidity")} value={live.humidity_pct != null ? `${live.humidity_pct} %` : "—"} hint="" />
        </div>
      </div>

      {/* Sun / Moon / Tides */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="ledger-card p-5" data-testid="weather-sun">
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("weather.sun")}</div>
          <div className="mt-2 flex items-center gap-3">
            <Sunrise className="w-6 h-6 text-[#F59E0B]" />
            <div className="flex-1">
              <div className="text-xs text-[#94A3B8]">{t("weather.sunrise")}</div>
              <div className="font-mono text-lg">{fmtTime(live.forecast?.[0]?.sunrise)}</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Sunset className="w-6 h-6 text-[#F97316]" />
            <div className="flex-1">
              <div className="text-xs text-[#94A3B8]">{t("weather.sunset")}</div>
              <div className="font-mono text-lg">{fmtTime(live.forecast?.[0]?.sunset)}</div>
            </div>
          </div>
          {live.forecast?.[0]?.uv_index_max != null && (
            <div className="mt-3 flex items-center gap-3">
              <Sun className="w-6 h-6 text-[#EAB308]" />
              <div className="flex-1">
                <div className="text-xs text-[#94A3B8]">{t("weather.uvMax")}</div>
                <div className="font-mono text-lg">{live.forecast[0].uv_index_max}</div>
              </div>
            </div>
          )}
        </div>

        <div className="ledger-card p-5" data-testid="weather-moon">
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("weather.moon")}</div>
          <div className="mt-2 flex items-center gap-3">
            <Moon className="w-6 h-6 text-[#A78BFA]" />
            <div className="flex-1">
              <div className="text-xs text-[#94A3B8]">{t(`weather.phase.${live.moon?.label || "new_moon"}`)}</div>
              <div className="font-mono text-lg">{Math.round((live.moon?.illumination ?? 0) * 100)}%</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-[#64748B]">{t("weather.moonHint")}</div>
        </div>

        <div className="ledger-card p-5" data-testid="weather-tides">
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("weather.tides")}</div>
          {(live.tides || []).length === 0 && <div className="mt-2 text-sm text-[#64748B]">{t("weather.tidesNone")}</div>}
          <div className="mt-2 space-y-1.5">
            {(live.tides || []).slice(0, 4).map((ev, i) => (
              <div key={i} className="flex items-center justify-between font-mono text-xs">
                <div className="flex items-center gap-2">
                  {ev.type === "high" ? <TrendingUp className="w-3.5 h-3.5 text-[#34D399]" /> : <TrendingDown className="w-3.5 h-3.5 text-[#60A5FA]" />}
                  <span className="text-[#F8FAFC]">{t(`weather.tide.${ev.type}`)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#94A3B8]">{fmtDay(ev.time)} {fmtTime(ev.time)}</span>
                  <span className="text-[#F8FAFC]">{ev.height_m} m</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 7-day forecast */}
      <div className="ledger-card p-5" data-testid="weather-forecast">
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-3">{t("weather.forecast7d")}</div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {(live.forecast || []).map((d) => (
            <div key={d.date} className="bg-[#050A14] border border-[#1E293B] rounded-md p-3" data-testid={`weather-fc-${d.date}`}>
              <div className="font-mono text-[10px] text-[#94A3B8]">{fmtDay(d.date)}</div>
              <div className="mt-1 text-[11px] text-[#F8FAFC]">{WMO_EN[d.weather_code] || "—"}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-mono text-lg text-[#F8FAFC]">{d.temp_max_c?.toFixed(0) ?? "—"}°</span>
                <span className="font-mono text-xs text-[#64748B]">/ {d.temp_min_c?.toFixed(0) ?? "—"}°</span>
              </div>
              <div className="mt-1 font-mono text-[11px] text-[#94A3B8] flex items-center gap-1"><Wind className="w-3 h-3"/> {d.wind_max_kt ?? "—"} kt</div>
              <div className="mt-0.5 font-mono text-[11px] text-[#94A3B8] flex items-center gap-1"><Waves className="w-3 h-3"/> {d.wave_max_m ?? "—"} m</div>
              {d.precip_prob_pct != null && <div className="mt-0.5 font-mono text-[11px] text-[#4DA6FF] flex items-center gap-1"><Droplets className="w-3 h-3"/> {d.precip_prob_pct}%</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, hint }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-[#4DA6FF] mt-0.5" />
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{label}</div>
        <div className="font-display text-xl font-bold text-[#F8FAFC]">{value}</div>
        {hint && <div className="font-mono text-[10px] text-[#64748B]">{hint}</div>}
      </div>
    </div>
  );
}
